/**
 * Voice guide for face capture: step, lighting, capture guidance, aligned, captured.
 * iOS Safari requires a user gesture to unlock speechSynthesis. Call unlockForIOS()
 * from a click/touch handler - it must speak a real utterance (not silent) in that same
 * handler. Also requires voices to be loaded (iOS loads them async).
 */

import { useCallback, useEffect, useRef } from 'react';

const VOICE_TEXT = {
  front: 'Look straight at the camera. Keep your head level and hold still.',
  left: 'Turn your head to the right. Keep your head level.',
  right: 'Turn your head to the left. Keep your head level.',
  aligned: 'Hold still. Capturing.',
  captured: 'Captured.',
};

const GUIDANCE_TO_VOICE = {
  'Position your face': 'Position your face in the frame.',
  'Move face left': 'Move your face to the left.',
  'Move face right': 'Move your face to the right.',
  'Move closer': 'Move closer to the camera.',
  'Move a little farther': 'Move a little farther from the camera.',
  'Tilt head right': 'Tilt your head to the right.',
  'Tilt head left': 'Tilt your head to the left.',
  'Lower your head': 'Lower your head slightly.',
  'Raise your head': 'Raise your head slightly.',
  'Perfect alignment!': 'Perfect. Hold still.',
  'Perfect! Hold still.': 'Perfect. Hold still.',
  'Turn your head to the right': 'Turn your head to the right. Keep it level.',
  'Turn your head to the left': 'Turn your head to the left. Keep it level.',
  'Keep head level': 'Keep your head level.',
  'Adjust your position': 'Adjust your position in the frame.',
};

const GUIDANCE_THROTTLE_MS = 4000;
const LIGHTING_THROTTLE_MS = 5000;


const FEMALE_VOICE_NAMES = ['female', 'zira', 'samantha', 'victoria', 'karen', 'aria', 'susan', 'hazel'];

function pickVoice(voices) {
  const femaleVoice = voices.find((v) =>
    v.lang.startsWith('en') && FEMALE_VOICE_NAMES.some((n) => v.name.toLowerCase().includes(n))
  );
  return femaleVoice || voices.find((v) => v.lang.startsWith('en')) || voices[0];
}

export function useVoiceGuide(guidedStep, isAligned, isCapturing, enabled = true, lightingOk = true, lightingMessage = '', guidanceMessage = '', lightingReady = true) {
  const lastStepRef = useRef(null);
  const lastAlignedRef = useRef(false);
  const lastCapturingRef = useRef(false);
  const lastGuidanceRef = useRef('');
  const lastLightingRef = useRef(true);
  const lastGuidanceTimeRef = useRef(0);
  const lastLightingTimeRef = useRef(0);
  const enabledRef = useRef(enabled);
  const capturedSpeechTimeoutRef = useRef(null);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const check = () => {
      const voices = window.speechSynthesis.getVoices();
      return voices.length > 0;
    };
    if (!check()) {
      window.speechSynthesis.onvoiceschanged = () => {
        check();
        window.speechSynthesis.onvoiceschanged = null;
      };
      const t = setInterval(() => { if (check()) clearInterval(t); }, 100);
      return () => { clearInterval(t); window.speechSynthesis.onvoiceschanged = null; };
    }
  }, []);

  const speak = useCallback((text, opts = {}) => {
    if (typeof window === 'undefined' || !window.speechSynthesis || !text || !String(text).trim()) return;
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(String(text).trim());
    utterance.rate = opts.rate ?? 0.95;
    utterance.pitch = opts.pitch ?? 1;
    utterance.volume = opts.volume ?? 1;
    utterance.lang = opts.lang ?? 'en-US';
    const enVoice = pickVoice(voices);
    if (enVoice) utterance.voice = enVoice;
    window.speechSynthesis.speak(utterance);
  }, []);

  const unlockForIOS = useCallback((textToSpeak) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return;
    const text = (textToSpeak || VOICE_TEXT[guidedStep] || 'Voice guide enabled.').trim();
    if (!text) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.volume = 1;
      u.rate = 0.95;
      u.lang = 'en-US';
      const enVoice = pickVoice(voices);
      if (enVoice) u.voice = enVoice;
      window.speechSynthesis.speak(u);
    } catch (_) { /* ignore */ }
  }, [guidedStep]);

  useEffect(() => {
    if (!enabled && typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, [enabled]);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !window.speechSynthesis) return;
    const text = VOICE_TEXT[guidedStep];
    if (text && lastStepRef.current !== guidedStep) {
      lastStepRef.current = guidedStep;
      speak(text);
    }
  }, [guidedStep, speak, enabled]);

  useEffect(() => {
    if (!enabled || !isAligned || lastAlignedRef.current) return;
    lastAlignedRef.current = true;
    speak(VOICE_TEXT.aligned);
  }, [isAligned, speak, enabled]);

  useEffect(() => {
    if (!isAligned) lastAlignedRef.current = false;
  }, [isAligned]);

  useEffect(() => {
    if (!enabled || !isCapturing || lastCapturingRef.current) return;
    lastCapturingRef.current = true;
    if (capturedSpeechTimeoutRef.current) {
      clearTimeout(capturedSpeechTimeoutRef.current);
      capturedSpeechTimeoutRef.current = null;
    }
    capturedSpeechTimeoutRef.current = setTimeout(() => {
      capturedSpeechTimeoutRef.current = null;
      if (enabledRef.current) speak(VOICE_TEXT.captured);
    }, 100);
    return () => {
      if (capturedSpeechTimeoutRef.current) {
        clearTimeout(capturedSpeechTimeoutRef.current);
        capturedSpeechTimeoutRef.current = null;
      }
    };
  }, [isCapturing, speak, enabled]);

  useEffect(() => {
    if (!isCapturing) lastCapturingRef.current = false;
  }, [isCapturing]);

  useEffect(() => {
    if (!enabled || !lightingReady || typeof window === 'undefined' || !window.speechSynthesis) return;
    if (isAligned || isCapturing) return;
    if (lightingOk) {
      lastLightingRef.current = true;
      return;
    }
    const now = Date.now();
    const msg = (lightingMessage || '').trim() || 'Improve lighting. Move to a brighter area with even light.';
    if (lastLightingRef.current !== false || now - lastLightingTimeRef.current >= LIGHTING_THROTTLE_MS) {
      lastLightingRef.current = false;
      lastLightingTimeRef.current = now;
      speak(msg);
    }
  }, [enabled, lightingReady, lightingOk, lightingMessage, isAligned, isCapturing, speak]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !window.speechSynthesis) return;
    if (isAligned || isCapturing || !lightingOk) return;
    const msg = (guidanceMessage || '').trim();
    if (!msg) return;
    const voiceText = GUIDANCE_TO_VOICE[msg] || msg;
    const now = Date.now();
    if (voiceText && voiceText !== lastGuidanceRef.current && now - lastGuidanceTimeRef.current >= GUIDANCE_THROTTLE_MS) {
      lastGuidanceRef.current = voiceText;
      lastGuidanceTimeRef.current = now;
      speak(voiceText);
    }
  }, [enabled, guidanceMessage, isAligned, isCapturing, lightingOk, speak]);

  return { speak, unlockForIOS };
}
